// #70 `diff.revise` (DC-21; debrief D-5, interview V-12): the editable form of G5's two-column Diff, used inside
// *Revise own score*. Each scored section is one row: its name, the *Original* value on the left — locked, read-only,
// with the lock glyph and his rule "Audit all original/revised values." as the column's description (the D state of
// the row) — and on the right the *Revised* value as G2's RatingScale. Under the rows the recommendation the same
// way (the original's pill · G2's RecoTiles in its playbook rows, one per offered recommendation in the requisition's
// display order, B14 C6; words and tones from recommendationWord), then the *Rationale*
// TextArea. A changed revised value is marked on --t-gold, never red or green. `mode="view"` shows both columns as
// values (a submitted revision). ReviseOwnScore wraps it in G4b's Dialog with the *Revise own score* preset.

import { useId, type ReactNode } from "react";
import { Field, Icon, Pill, TAG_LABELS, TextArea, type Blocked } from "../core";
import { ACTION_LABELS, recommendationWord, recoPillTone } from "../labels";
import { cx } from "../lib/cx";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { RatingScale, RecoTiles, SCORECARD_LABELS, ScoreCell, type PlaybookRecoOption, type RatingValue, type ScoreValue } from "../g2";
import { Dialog, type OverlayCloseReason, type OverlayRefused } from "../g4b";
import { DIFF_LABELS } from "../g5";
import type { Hs } from "../lib/hs";

/** A stored recommendation value ("hire", "no_hire", "strong_hire", …; C6 widened it from the two client keys). */
export type RevisedReco = string;

/** The offered set when the caller gives none: a level-v1 requisition's Hire · No Hire. */
export const DEFAULT_OFFERED: readonly string[] = ["hire", "no_hire"];

export interface DiffReviseRow {
  key: string;
  /** The section's name: a ROOTS value, a skill, Skill #n. */
  label: string;
  original: RatingValue | null;
  revised?: RatingValue | null | undefined;
  /** The pinned guide's descriptors for this section. */
  descriptors?: Partial<Record<ScoreValue, ReactNode>> | undefined;
  hs?: Partial<Record<RatingValue, Hs>> | undefined;
}

export interface DiffReviseProps {
  rows: readonly DiffReviseRow[];
  onRate?: ((key: string, value: RatingValue) => void) | undefined;
  originalReco?: RevisedReco | undefined;
  revisedReco?: RevisedReco | null | undefined;
  /** The requisition's offered recommendations (level.offered), in display order. */
  offered?: readonly string[] | undefined;
  onReco?: ((value: RevisedReco) => void) | undefined;
  rationale?: string | undefined;
  onRationale?: ((value: string) => void) | undefined;
  /** The rationale's required sentence, when it is missing. */
  rationaleError?: ReactNode | undefined;
  /** The revision cannot be made (his rule): every revised control dashed and described. */
  blocked?: Blocked | undefined;
  /** "view": a submitted revision, both columns as values. */
  mode?: "edit" | "view" | undefined;
  can?: boolean | undefined;
  hs?: { rationale?: Hs | undefined } | undefined;
  className?: string | undefined;
}

/** The offered values as playbook rows; an unknown value is dropped, never guessed. */
function offeredRows(offered: readonly string[], blocked: Blocked | undefined): PlaybookRecoOption[] {
  return offered.flatMap((value) => {
    const word = recommendationWord(value);
    return word ? [{ value, label: word.label, tone: word.tone, blocked: blocked ? { reason: blocked.reason } : undefined }] : [];
  });
}

export function DiffRevise({
  rows, onRate, originalReco, revisedReco, offered = DEFAULT_OFFERED, onReco, rationale, onRationale, rationaleError, blocked, mode, can, hs = {}, className,
}: DiffReviseProps) {
  const id = useId();
  if (can === false) return null;
  const original = recommendationWord(originalReco);
  const revisedWord = recommendationWord(revisedReco);
  const view = mode === "view";
  const recoChanged = Boolean(revisedReco && originalReco && revisedReco !== originalReco);
  return (
    <div className={cx("mc-diff", "mc-diff-revise", className)}>
      <div className="mc-drev-head" aria-hidden="true">
        <span />
        <span className="mc-drev-oh"><Icon name="lock" size={14} />{DIFF_LABELS.original}</span>
        <span className="mc-drev-nh">{DIFF_LABELS.revised}</span>
      </div>
      <VisuallyHidden>{DIFF_LABELS.original}{": "}{DIFF_LABELS.auditOriginalRevised}</VisuallyHidden>
      {rows.map((row) => {
        const changed = row.revised !== undefined && row.revised !== null && row.revised !== row.original;
        return (
          <div key={row.key} className={cx("mc-drev-row", changed && "mc-drev-ch")} role="group" aria-labelledby={`${id}-${row.key}`}>
            <b className="mc-drev-k" id={`${id}-${row.key}`}>
              {row.label}
              {changed ? <VisuallyHidden>{", "}{TAG_LABELS.revised}</VisuallyHidden> : null}
            </b>
            <span className="mc-drev-o">
              <VisuallyHidden>{DIFF_LABELS.original}{" "}</VisuallyHidden>
              <ScoreCell score={row.original} />
            </span>
            <span className="mc-drev-n">
              {view ? <VisuallyHidden>{DIFF_LABELS.revised}{" "}</VisuallyHidden> : null}
              <RatingScale
                label={row.label}
                value={row.revised ?? null}
                onChange={(value) => onRate?.(row.key, value)}
                descriptors={row.descriptors}
                blocked={blocked}
                mode={view ? "view" : undefined}
                hs={row.hs}
              />
            </span>
          </div>
        );
      })}
      <div className={cx("mc-drev-row", "mc-drev-reco", recoChanged && "mc-drev-ch")} role="group" aria-labelledby={`${id}-reco`}>
        <b className="mc-drev-k" id={`${id}-reco`}>
          {SCORECARD_LABELS.recommendation}
          {recoChanged ? <VisuallyHidden>{", "}{TAG_LABELS.revised}</VisuallyHidden> : null}
        </b>
        <span className="mc-drev-o">
          <VisuallyHidden>{DIFF_LABELS.original}{" "}</VisuallyHidden>
          {original ? <Pill tone={recoPillTone(original.tone)}>{original.label}</Pill> : <span className="mc-t3">{"—"}</span>}
        </span>
        <span className="mc-drev-n">
          {view ? (
            <>
              <VisuallyHidden>{SCORECARD_LABELS.recommendation}{" "}</VisuallyHidden>
              {revisedWord ? <Pill tone={recoPillTone(revisedWord.tone)}>{revisedWord.label}</Pill> : <span className="mc-t3">{"—"}</span>}
            </>
          ) : (
            <RecoTiles
              form="playbook"
              options={offeredRows(offered, blocked)}
              value={revisedReco ?? null}
              onChange={(value) => onReco?.(value)}
              disabled={blocked !== undefined}
            />
          )}
        </span>
      </div>
      <Field label={DIFF_LABELS.rationale} required={!view} error={rationaleError} blocked={blocked} mode={view ? "view" : undefined} className="mc-drev-why">
        <TextArea rows={3} value={rationale ?? ""} onChange={(event) => onRationale?.(event.target.value)} hs={hs.rationale} />
      </Field>
    </div>
  );
}

export interface ReviseOwnScoreProps extends DiffReviseProps {
  open?: boolean | undefined;
  onClose?: ((reason: OverlayCloseReason) => void) | undefined;
  onSubmit?: (() => void) | undefined;
  submitting?: boolean | undefined;
  /** Submit is not possible yet (his rule as the description). */
  submitBlocked?: Blocked | undefined;
  refused?: OverlayRefused | undefined;
  /** A gallery section's static rendering of the dialog. */
  preview?: boolean | undefined;
  /** The dialog's capture hotspots: the close button and the primary. */
  dialogHs?: { close?: Hs | undefined; submit?: Hs | undefined } | undefined;
}

/** The *Revise own score* dialog holding the revision form (DS #60 preset, #70 .revise). */
export function ReviseOwnScore({ open, onClose, onSubmit, submitting, submitBlocked, refused, preview, dialogHs = {}, can, ...form }: ReviseOwnScoreProps) {
  return (
    <Dialog
      preset="reviseOwnScore"
      open={open}
      onClose={onClose}
      width={720}
      can={can}
      preview={preview}
      refused={refused}
      dismissLabel={ACTION_LABELS.cancel}
      primary={{ label: ACTION_LABELS.submit, onClick: onSubmit, blocked: submitBlocked, busy: submitting, hs: dialogHs.submit }}
      hs={dialogHs.close}
    >
      <DiffRevise {...form} />
    </Dialog>
  );
}
